import type { PersonalisationSlot } from '../context/personalisation-typedefs';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface OptionGroup {
  groupName: string;
  slots: PersonalisationSlot[];
}

export interface GroupedSlotModel {
  optionGroups: OptionGroup[];
  inputLineCount: number;
}

/** Shape of an alphanumeric slot value object (text + optional font/translation). */
export interface AlphanumericValue {
  text?: string;
  font?: string;
  translatedText?: string;
}

/**
 * Minimal selection shape required by the grouped slot model utilities.
 * Kept separate from the full SelectionSlot to avoid coupling this module to
 * the broader context types, and because SelectionSlot.selected is typed
 * too narrowly (string | null) for the varied runtime values.
 */
export interface GroupedModeSelection {
  id: string;
  selected: unknown;
  groupLineItemPropertyKey?: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Slot types supported in GROUPED slot mode. */
export const GROUPED_SUPPORTED_SLOT_TYPES = new Set<string>(['ALPHANUMERIC', 'IMAGE_UPLOAD']);

// ─── Private helpers ──────────────────────────────────────────────────────────

function sortSlotsByOrderIndex(slots: PersonalisationSlot[]): PersonalisationSlot[] {
  return [...slots].sort((slotA, slotB) => {
    const orderIndexA = Number(slotA.orderIndex) || 0;
    const orderIndexB = Number(slotB.orderIndex) || 0;
    return orderIndexA - orderIndexB;
  });
}

function hasFilledGroupedSelection(slot: PersonalisationSlot, selectedValue: unknown): boolean {
  if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
    return false;
  }

  if (slot.slotType === 'ALPHANUMERIC') {
    if (typeof selectedValue === 'string') {
      return selectedValue.trim().length > 0;
    }
    if (
      selectedValue &&
      typeof selectedValue === 'object' &&
      'text' in selectedValue &&
      typeof (selectedValue as AlphanumericValue).text === 'string'
    ) {
      return ((selectedValue as AlphanumericValue).text ?? '').trim().length > 0;
    }
    return false;
  }

  if (slot.slotType === 'IMAGE_UPLOAD') {
    return selectedValue instanceof File;
  }

  return Boolean(selectedValue);
}

// ─── Exports ──────────────────────────────────────────────────────────────────

/**
 * Builds the grouped slot model from step slots.
 * Distinct non-empty groupName values become Choose option groups.
 * Slots sharing a groupName are input lines within that option (ordered by orderIndex).
 * e.g. UV text: Black group has 3 slots → 3 input lines (line 1, 2, 3).
 */
export function buildGroupedSlotModel(slots: PersonalisationSlot[]): GroupedSlotModel {
  const groupsMap = new Map<string, PersonalisationSlot[]>();

  slots
    .filter((slot) => GROUPED_SUPPORTED_SLOT_TYPES.has(slot.slotType))
    .forEach((slot) => {
      const groupName = typeof slot.groupName === 'string' ? slot.groupName.trim() : '';
      if (!groupName) return;

      const existing = groupsMap.get(groupName) ?? [];
      existing.push(slot);
      groupsMap.set(groupName, existing);
    });

  const optionGroups: OptionGroup[] = Array.from(groupsMap.entries()).map(([groupName, groupSlots]) => ({
    groupName,
    slots: sortSlotsByOrderIndex(groupSlots)
  }));

  optionGroups.sort((groupA, groupB) => {
    const orderA = Number(groupA.slots[0]?.orderIndex) || 0;
    const orderB = Number(groupB.slots[0]?.orderIndex) || 0;
    return orderA - orderB;
  });

  const inputLineCount = optionGroups.reduce((max, group) => Math.max(max, group.slots.length), 0);

  return { optionGroups, inputLineCount };
}

/** Returns the backend slot for a given input line index within an option group. */
export function getSlotForInputLine(
  optionGroup: OptionGroup,
  inputLineIndex: number
): PersonalisationSlot | undefined {
  return optionGroup.slots[inputLineIndex];
}

/** Returns all slot IDs for an option group. */
export function getOptionGroupSlotIds(optionGroup: OptionGroup): string[] {
  return optionGroup.slots.map((slot) => slot.id);
}

/** Returns every slot ID on the step except those in the given option group. */
export function getInactiveGroupSlotIds(optionGroups: OptionGroup[], groupName: string): string[] {
  return optionGroups
    .filter((group) => group.groupName !== groupName)
    .flatMap((group) => getOptionGroupSlotIds(group));
}

/**
 * Infers the active option group name.
 *
 * For IMAGE_UPLOAD groups, images are propagated to every group slot to avoid
 * re-uploading on group switch. Inference by selection presence is therefore
 * unreliable — we use `groupLineItemPropertyKey` on the selection as the explicit
 * "user chose this group" signal instead.
 *
 * For text (ALPHANUMERIC) groups, only the active group's slots hold values, so
 * selection presence still works as before.
 */
export function inferActiveGroupName({
  optionGroups,
  selections
}: {
  optionGroups: OptionGroup[];
  selections: GroupedModeSelection[];
}): string {
  if (!optionGroups.length) return '';

  const hasImageGroups = optionGroups.some((group) =>
    group.slots.some((slot) => slot.slotType === 'IMAGE_UPLOAD')
  );

  if (hasImageGroups) {
    for (const optionGroup of optionGroups) {
      const isActive = optionGroup.slots.some((slot) => {
        const selection = selections.find((item) => item.id === slot.id);
        return selection?.groupLineItemPropertyKey === optionGroup.groupName;
      });
      if (isActive) return optionGroup.groupName;
    }
    return optionGroups[0].groupName;
  }

  for (const optionGroup of optionGroups) {
    const hasSelection = optionGroup.slots.some((slot) => {
      const selection = selections.find((item) => item.id === slot.id);
      return hasFilledGroupedSelection(slot, selection?.selected);
    });
    if (hasSelection) {
      return optionGroup.groupName;
    }
  }

  return optionGroups[0].groupName;
}

/**
 * Collects selection values per input line for an option group.
 * Array index corresponds to input line index.
 */
export function getInputLineValuesForGroup({
  optionGroup,
  selections,
  inputLineCount
}: {
  optionGroup: OptionGroup;
  selections: GroupedModeSelection[];
  inputLineCount: number;
}): Array<unknown> {
  const inputLineValues: Array<unknown> = [];

  for (let inputLineIndex = 0; inputLineIndex < inputLineCount; inputLineIndex++) {
    const slot = getSlotForInputLine(optionGroup, inputLineIndex);
    if (!slot) {
      inputLineValues.push(null);
      continue;
    }

    const selection = selections.find((item) => item.id === slot.id);
    inputLineValues.push(selection?.selected ?? null);
  }

  return inputLineValues;
}
